/** W011 — Settings → Suppressed Senders. Lists every sender Memso has
 *  stopped surfacing: explicit "Hide forever" rows plus the per-sender
 *  skip tallies that auto-suppressed after repeated skips. Each row is
 *  reversible, so the user is never permanently locked out of a sender
 *  they later change their mind about. */
import { useEffect, useState } from "react";
import { Ban, RotateCcw } from "lucide-react";
import { api, type SuppressedSender } from "../lib/api";

function describe(row: SuppressedSender): string {
  if (row.suppression_kind === "forever") return "Hidden forever";
  if (row.suppressed_at) {
    const until = row.expires_at ? new Date(row.expires_at).toLocaleDateString() : "30 days";
    return `Suppressed after ${row.skip_count} skips · until ${until}`;
  }
  return `${row.skip_count} skip${row.skip_count === 1 ? "" : "s"} so far`;
}

export function SuppressedSendersSection() {
  const [rows, setRows] = useState<SuppressedSender[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    api
      .listSuppressions()
      .then((d) => setRows(d.suppressions))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const remove = async (id: string) => {
    if (busy) return;
    setBusy(id);
    try {
      await api.removeSuppression(id);
      load();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Suppressed senders</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Senders Memso has stopped surfacing, either because you hid them or because you kept
          skipping them. Reverse any of these to start seeing them again.
        </p>
      </header>

      {loading ? (
        <p className="text-sm italic text-slate-500 dark:text-slate-400">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm italic text-slate-500 dark:text-slate-400">
          Nothing suppressed. When you hide a sender or skip one repeatedly, it shows up here.
        </p>
      ) : (
        <ul className="divide-y divide-slate-200 overflow-hidden rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-800">
          {rows.map((row) => (
            <li key={row.id} className="flex items-center gap-3 bg-white p-3 dark:bg-slate-900">
              <Ban
                className={`h-4 w-4 flex-shrink-0 ${
                  row.suppression_kind === "forever" ? "text-rose-500" : "text-amber-500"
                }`}
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                  {row.sender}
                  {row.kind !== "*" && (
                    <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-400">
                      {row.kind}
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">{describe(row)}</div>
              </div>
              <button
                type="button"
                onClick={() => remove(row.id)}
                disabled={busy === row.id}
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Unsuppress
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
